import { useState, useEffect } from 'react';
import { Check } from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface StyleEditorDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    style?: ManuscriptMarkStyle;
    onSave: (style: Partial<ManuscriptMarkStyle>) => void;
    onDelete?: () => void;
}

const COLORS = [
    { name: 'Yellow', value: 'rgb(234, 179, 8)' },
    { name: 'Green', value: 'rgb(34, 197, 94)' },
    { name: 'Blue', value: 'rgb(59, 130, 246)' },
    { name: 'Pink', value: 'rgb(236, 72, 153)' },
    { name: 'Orange', value: 'rgb(249, 115, 22)' },
    { name: 'Purple', value: 'rgb(168, 85, 247)' },
    { name: 'Teal', value: 'rgb(20, 184, 166)' },
];

const TEXTURES: { id: ManuscriptMarkTexture; label: string }[] = [
    { id: 'solid', label: 'Solid' },
    { id: 'pencil', label: 'Pencil' },
    { id: 'marker', label: 'Marker' },
    { id: 'chalk', label: 'Chalk' },
];

const BORDER_TYPES = [
    { id: 'highlight', label: 'None' },
    { id: 'underline', label: 'Underline' },
    { id: 'box', label: 'Box' },
    { id: 'circle', label: 'Circle' },
];

const THICKNESSES = [
    { label: 'Small', value: 1 },
    { label: 'Medium', value: 2 },
    { label: 'Large', value: 4 },
];

export function StyleEditorDialog({ open, onOpenChange, style, onSave, onDelete }: StyleEditorDialogProps) {
    const [name, setName] = useState(style?.name || '');
    const [color, setColor] = useState(style?.color || COLORS[0].value);
    const [texture, setTexture] = useState<ManuscriptMarkTexture>(style?.texture || 'pencil');
    const [borderType, setBorderType] = useState<ManuscriptMarkType>(style?.type || 'highlight');
    const [thickness, setThickness] = useState<number>(style?.thickness || 2);

    useEffect(() => {
        if (style) {
            setName(style.name);
            setColor(style.color);
            setTexture(style.texture);
            setBorderType(style.type);
            setThickness(style.thickness || 2);
        } else if (open) {
            // Reset to defaults when creating a new style
            setName('');
            setColor(COLORS[0].value);
            setTexture('pencil');
            setBorderType('highlight');
            setThickness(2);
        }
    }, [style, open]);

    const handleSave = () => {
        onSave({
            name,
            color,
            texture,
            type: borderType as any,
            thickness,
        });
        onOpenChange(false);
    };

    const handleDelete = () => {
        onDelete?.();
        onOpenChange(false);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>{style ? 'Edit Style' : 'New Custom Style'}</DialogTitle>
                    <DialogDescription className="text-xs text-muted-foreground pt-1 italic">
                        Customize how this mark appears in your manuscript.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    {/* Live Preview */}
                    <div className="space-y-2">
                        <Label>Preview</Label>
                        <div className="p-4 bg-muted rounded-md">
                            <span
                                className={`mark mark-texture-${texture} mark-${borderType} mark-is-start mark-is-end`}
                                style={{
                                    '--mark-color': color.includes('rgba')
                                        ? color
                                        : color.replace('rgb', 'rgba').replace(')', ', 0.35)'),
                                    '--mark-thickness': `${thickness}px`
                                } as any}
                            >In the beginning was the Word</span>
                        </div>
                    </div>

                    {/* Name Input */}
                    <div className="space-y-2">
                        <Label htmlFor="style-name">Name</Label>
                        <Input
                            id="style-name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="e.g., Action Verbs"
                        />
                    </div>

                    <div className="space-y-2">
                        <Label className="text-[10px] font-bold text-muted-foreground/60 uppercase tracking-widest px-1">Color Choice</Label>
                        <div className="flex flex-wrap gap-3 p-1">
                            {COLORS.map((c) => (
                                <button
                                    key={c.value}
                                    onClick={() => setColor(c.value)}
                                    className={`
                                        group relative h-8 w-8 rounded-full border-2 transition-all duration-200
                                        ${color === c.value
                                            ? 'border-brand scale-110 shadow-md ring-2 ring-brand/20 ring-offset-2'
                                            : 'border-transparent hover:scale-110 hover:shadow-sm'
                                        }
                                    `}
                                    style={{ backgroundColor: c.value }}
                                    title={c.name}
                                >
                                    {color === c.value && (
                                        <div className="absolute inset-0 flex items-center justify-center">
                                            <Check className="h-4 w-4 text-white drop-shadow-md" />
                                        </div>
                                    )}
                                    <span className="sr-only">{c.name}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Texture Picker */}
                    <div className="space-y-2">
                        <Label className="text-[10px] font-bold text-muted-foreground/60 uppercase tracking-widest px-1">Texture</Label>
                        <div className="grid grid-cols-4 gap-2">
                            {TEXTURES.map((t) => (
                                <button
                                    key={t.id}
                                    onClick={() => setTexture(t.id)}
                                    className={`
                                        group relative flex flex-col items-center gap-1.5 p-2 rounded-lg border-2 transition-all duration-200
                                        ${texture === t.id
                                            ? 'border-brand bg-brand/5 shadow-sm'
                                            : 'border-muted-foreground/10 hover:border-muted-foreground/20'
                                        }
                                    `}
                                >
                                    <div className="w-full h-10 rounded bg-muted/30 flex items-center justify-center overflow-hidden">
                                        <span
                                            className={`mark mark-texture-${t.id} mark-highlight mark-is-start mark-is-end w-full text-center text-[11px] font-black py-1`}
                                            style={{
                                                '--mark-color': color.includes('rgba')
                                                    ? color
                                                    : color.replace('rgb', 'rgba').replace(')', ', 0.35)'),
                                            } as any}
                                        >
                                            STYLE
                                        </span>
                                    </div>
                                    <span className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground">{t.label}</span>
                                    {texture === t.id && (
                                        <div className="absolute top-1 right-1">
                                            <div className="bg-brand rounded-full p-0.5 shadow-sm">
                                                <Check className="h-2 w-2 text-white" />
                                            </div>
                                        </div>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Border Style Picker */}
                    <div className="space-y-2">
                        <Label className="text-[10px] font-bold text-muted-foreground/60 uppercase tracking-widest px-1">Border Style</Label>
                        <div className="grid grid-cols-4 gap-2">
                            {BORDER_TYPES.map((b) => (
                                <button
                                    key={b.id}
                                    onClick={() => setBorderType(b.id as ManuscriptMarkType)}
                                    className={`
                                        group relative flex flex-col items-center gap-1.5 p-2 rounded-lg border-2 transition-all duration-200
                                        ${borderType === b.id
                                            ? 'border-brand bg-brand/5 shadow-sm'
                                            : 'border-muted-foreground/10 hover:border-muted-foreground/20'
                                        }
                                    `}
                                >
                                    <div className="w-full h-10 rounded bg-muted/30 flex items-center justify-center overflow-hidden px-1">
                                        <span
                                            className={`mark mark-texture-${texture} mark-${b.id} mark-is-start mark-is-end w-full text-center text-[11px] font-black py-1`}
                                            style={{
                                                '--mark-color': color.includes('rgba')
                                                    ? color
                                                    : color.replace('rgb', 'rgba').replace(')', ', 0.35)'),
                                                '--mark-thickness': '2px'
                                            } as any}
                                        >
                                            WORD
                                        </span>
                                    </div>
                                    <span className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground">{b.label}</span>
                                    {borderType === b.id && (
                                        <div className="absolute top-1 right-1">
                                            <div className="bg-brand rounded-full p-0.5 shadow-sm">
                                                <Check className="h-2 w-2 text-white" />
                                            </div>
                                        </div>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Thickness Picker */}
                    {borderType !== 'highlight' && (
                        <div className="space-y-2">
                            <Label className="text-[10px] font-bold text-muted-foreground/60 uppercase tracking-widest px-1">Thickness</Label>
                            <div className="grid grid-cols-3 gap-2">
                                {THICKNESSES.map((t) => (
                                    <button
                                        key={t.value}
                                        onClick={() => setThickness(t.value)}
                                        className={`
                                            group relative flex flex-col items-center gap-1.5 p-2 rounded-lg border-2 transition-all duration-200
                                            ${thickness === t.value
                                                ? 'border-brand bg-brand/5 shadow-sm'
                                                : 'border-muted-foreground/10 hover:border-muted-foreground/20'
                                            }
                                        `}
                                    >
                                        <div className="w-full h-8 rounded bg-muted/50 flex items-center justify-center">
                                            <div
                                                className="w-12 bg-muted-foreground/40 rounded-full"
                                                style={{ height: `${t.value}px` }}
                                            />
                                        </div>
                                        <span className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground">{t.label}</span>
                                        {thickness === t.value && (
                                            <div className="absolute top-1 right-1">
                                                <div className="bg-brand rounded-full p-0.5 shadow-sm">
                                                    <Check className="h-2 w-2 text-white" />
                                                </div>
                                            </div>
                                        )}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Actions */}
                    <div className="flex justify-between pt-4">
                        {onDelete && style && style.isCustom ? (
                            <Button variant="destructive" onClick={handleDelete}>
                                Delete
                            </Button>
                        ) : (
                            <div />
                        )}
                        <Button onClick={handleSave}>Save</Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
